<template>
  <div class="filter-button-wrapper mb-300">
    <LcButton
      class="rounded-m w-full border-none bg-neutral-white"
      icon="filter"
      size="medium"
      variant="raised"
      @click="handleOpenFilter"
    >
      {{ $t('results.filter') }}
    </LcButton>

    <LcButton
      v-if="activeFilters.length > 0"
      class="filter-reset h-300 caption-bold"
      icon="trash-full"
      icon-position="right"
      size="small"
      @click="handleFilterReset"
    >
      {{ activeFilters.length }}
    </LcButton>
  </div>

  <div v-if="config.days.length > 0">
    <LcRemoteList
      v-bind="config"
      :button-text="$t('results.button.loadmore')"
      class="mb-200"
      data-containerid="results_list"
      :loading="isFetchMoreLoading"
      @fetch-more="invokeFetchMore"
    >
      <li v-for="group of config.days" :key="group.date">
        <span class="flex items-center gap-100 mb-100">
          <LcIcon name="calendar" />
          <DateTime
            class="font-medium text-capitalize"
            :datetime="getZonedDate(group.date)"
            format="result"
            :locale="locale"
          />
        </span>

        <ul class="flex flex-col gap-100">
          <li v-for="draw of group.draws" :key="draw.id">
            <ResultsListItem v-bind="draw" :time-zone="result?.drawListByDay.timeZone" />
          </li>
        </ul>
      </li>
    </LcRemoteList>

    <LcInfoText class="pt-400 px-100 pb-200" size="small" :text="$t('results.disclaimer')" />
  </div>

  <LazyNoResults
    v-else-if="!loading"
    :headline="$t('results.noresults.headline')"
    @cta-click="refetch"
  />

  <ResultsFilterModal v-model:is-visible="isFilterVisible" v-model:value="activeFilters" />
</template>

<script lang="ts" setup>
import { parse } from 'date-fns'
import { fromZonedTime } from 'date-fns-tz'

import type { DrawResultsQuery } from '#api/shared/@types/generated/backend/graphql-schema-types'

const ONE_DAY = 1

definePageMeta({ layout: 'default' })

const { locale } = useI18n()
const licensedTerritory = useLicensedTerritory({ raw: true })
const route = useRoute()
const router = useRouter()

const isFetchMoreLoading = ref(false)
const isFilterVisible = ref(false)

const activeFilters = computed({
  get: () => {
    const { lotteries } = route.query
    if (!lotteries) return []

    return filterIncorrectLotteryIds(licensedTerritory, lotteries)
  },
  set: (newValue) => {
    if (!newValue.length) {
      return router.replace({ query: {} })
    }

    return router.replace({
      query: {
        lotteries: filterIncorrectLotteryIds(licensedTerritory, newValue),
      },
    })
  },
})

const { fetchMore, loading, onError, onResult, refetch, result } = useDrawResultsQuery(
  {
    filter: {
      lotteries: activeFilters.value.length ? activeFilters.value : undefined,
    },
    licensedTerritory,
    limit: ONE_DAY,
    offset: 0,
  },
  { fetchPolicy: 'cache-first' },
)

const config = computed(() => {
  const draws = result.value?.drawListByDay
  const days = draws?.days || []

  return {
    count: days.length,
    days,
    total: draws?.count || 0,
  }
})
const timeZone = computed(() => result.value?.drawListByDay.timeZone ?? 'America/New_York')

const getZonedDate = (date: string) => {
  const parsedDate = parse(date, 'yyyy-MM-dd', new Date())
  const utcDate = fromZonedTime(parsedDate, timeZone.value)

  return utcDate
}

const handleFilterReset = () => {
  router.replace({ query: {} })
  refetch({
    filter: {},
    licensedTerritory,
    limit: ONE_DAY,
    offset: 0,
  })
}

const handleOpenFilter = () => {
  isFilterVisible.value = true
}

const invokeFetchMore = () => {
  isFetchMoreLoading.value = true

  fetchMore({
    updateQuery: (previousResult, { fetchMoreResult }) => {
      if (!fetchMoreResult) return previousResult
      return mergeResults(previousResult, fetchMoreResult)
    },
    variables: { offset: config.value.count },
  })
}

const mergeResults = (previous: DrawResultsQuery, latest: DrawResultsQuery) => {
  const oldResults = previous.drawListByDay.days
  const newResults = latest.drawListByDay.days

  return {
    drawListByDay: {
      ...previous.drawListByDay,
      days: [...oldResults, ...newResults],
    },
  }
}

const stopFetchMoreLoading = () => {
  isFetchMoreLoading.value = false
}

onError(stopFetchMoreLoading)
onResult(stopFetchMoreLoading)

onMounted(() => {
  const { lotteries } = route.query
  if (!lotteries) return

  router.replace({
    query: {
      lotteries: filterIncorrectLotteryIds(licensedTerritory, lotteries),
    },
  })
})

watch(activeFilters, (newValue) => {
  refetch({
    filter: {
      lotteries: newValue.length ? newValue : undefined,
    },
    licensedTerritory,
    limit: ONE_DAY,
    offset: 0,
  })
})
</script>

<style scoped lang="scss">
.filter-button-wrapper {
  position: relative;

  .filter-reset {
    position: absolute;
    top: 50%;
    right: token('size/space/gap/100');
    padding: token('size/space/gap/050') token('size/space/gap/150');
    border: none;
    background-color: token('color/background/accent/info/info/bold');
    box-shadow: token('shadow/shadow/card/small');
    color: token('color/icon/primary/primary');
    transform: translate(0, -50%);

    @include hover-state {
      border-color: token('color/border/secondary/secondary');
      box-shadow:
        token('shadow/shadow/card/small'),
        0 0 0 0.125rem token('color/lightblue/400');
    }

    :deep(.lc-icon) {
      font-size: token('font/size/m');
    }
  }
}
</style>
